"use client"

import { useRef, useState } from "react"
import { Mail } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Switch } from "@/components/ui/switch"
import type { MessageAgentChannel } from "@/hooks/agents/messaging/use-agents"
import { OFFLINE_MESSAGE_MAX_LENGTH } from "@/lib/agents/messaging/types"
import type { ChannelOfflineMessageConfig } from "@/lib/agents/messaging/types"
import {
  DEFAULT_OFFLINE_MESSAGE,
  resolveOfflineMessage,
} from "@/lib/agents/messaging/channels/shared/offline-message"
import { useUpdateMessagingChannelOfflineMessageMutation } from "@/hooks/mutations/messaging-channels"

interface ChannelOfflineMessageControlProps {
  agentId: string
  channel: MessageAgentChannel
  canManage: boolean
  onRefresh: () => void
  onError: (message: string) => void
}

/**
 * Row-level state (text, so it reads without colour) plus a popover for editing — the
 * setting is rarely changed and a full block per row buried the channel list.
 */
export function ChannelOfflineMessageControl({
  agentId,
  channel,
  canManage,
  onRefresh,
  onError,
}: ChannelOfflineMessageControlProps) {
  const saved = (channel.config?.offline_message ?? {}) as ChannelOfflineMessageConfig
  const resolved = resolveOfflineMessage({
    channel_type: channel.channel_type,
    config: channel.config ?? {},
  })
  const [text, setText] = useState(saved.text ?? "")
  const switchRef = useRef<HTMLButtonElement>(null)
  const updateOfflineMessage = useUpdateMessagingChannelOfflineMessageMutation()
  const isWidget = channel.channel_type === "web_widget"

  const save = async (next: ChannelOfflineMessageConfig) => {
    try {
      await updateOfflineMessage.mutateAsync({ agentId, channelId: channel.id, offlineMessage: next })
      onRefresh()
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to update offline message")
    }
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-7 gap-1.5 px-2 text-xs font-normal text-muted-foreground"
        >
          <Mail className="h-3.5 w-3.5" />
          <span>Offline:</span> <span>{resolved.enabled ? "on" : "off"}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <Label htmlFor={`offline-message-${channel.id}`} className="text-sm font-medium">
            {isWidget ? "Show offline notice" : "Send offline message"}
          </Label>
          <Switch
            ref={switchRef}
            id={`offline-message-${channel.id}`}
            checked={resolved.enabled}
            onCheckedChange={(enabled) => save({ enabled, text })}
            disabled={updateOfflineMessage.isPending || !canManage}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          {isWidget
            ? "Shown to visitors while nobody is answering here — AI replies off, or the agent disabled."
            : "Sent once per conversation session while nobody is answering here — AI replies off, or the agent disabled."}
        </p>
        {resolved.enabled && (
          <Input
            aria-label="Offline message text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onBlur={(e) => {
              // Focus moving to the switch: its own save carries the edited text,
              // and a concurrent blur PATCH could land last and re-enable it.
              if (e.relatedTarget === switchRef.current) return
              if ((saved.text ?? "") !== text) save({ enabled: true, text })
            }}
            placeholder={DEFAULT_OFFLINE_MESSAGE}
            maxLength={OFFLINE_MESSAGE_MAX_LENGTH}
            disabled={updateOfflineMessage.isPending || !canManage}
          />
        )}
      </PopoverContent>
    </Popover>
  )
}
